import { Close as CloseIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname } from "../../../utils";
import { INTEGRATION_LOGOS } from "../../integrations/constants";

interface BulkScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onScheduled: (count: number) => void;
  selectedContentIds: string[];
  companyId: string;
  getToken: Function;
  contentType?: string; // Optional: to filter integrations by type
}

export default ({
  open,
  onClose,
  onScheduled,
  selectedContentIds,
  companyId,
  getToken,
  contentType,
}: BulkScheduleDialogProps) => {
  const [publishAccount, setPublishAccount] = useState<string>("");
  const [integrations, setIntegrations] = useState<Array<any>>([]);
  const [isScheduling, setIsScheduling] = useState(false);

  // Helper function to get logo for integration type
  const getIntegrationLogo = (integrationType: string) => {
    return INTEGRATION_LOGOS[integrationType?.toLowerCase()] || null;
  };

  const getIntegrations = async () => {
    const currentToken = await getToken();

    // Determine which integration type to fetch based on content type
    let integrationType = "blog"; // Default to blog

    if (contentType === "linkedin_post" || contentType === "social_media_post") {
      integrationType = "linkedin";
    }

    try {
      const response = await axios.get(
        `${hostname}/integrations/by-type/${integrationType}`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      setIntegrations(response.data.items || []);
    } catch (error) {
      console.error("Error fetching integrations:", error);
      setIntegrations([]);
    }
  };

  const handleSchedule = async () => {
    if (!publishAccount) return;

    setIsScheduling(true);
    const currentToken = await getToken();
    try {
      const response = await axios.post(
        `${hostname}/content/bulk-schedule`,
        {
          companyId,
          contentIds: selectedContentIds,
          publishAccount,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      onScheduled(response.data.scheduled);
      onClose();
    } finally {
      setIsScheduling(false);
    }
  };

  useEffect(() => {
    if (open) {
      getIntegrations();
      setPublishAccount("");
    }
  }, [open]);

  if (!open) {
    return <></>;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Schedule Content
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Scheduling {selectedContentIds.length} content item
          {selectedContentIds.length > 1 ? "s" : ""} with:
        </Typography>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Publish Account</InputLabel>
          <Select
            label="Publish Account"
            value={publishAccount}
            onChange={(e) => setPublishAccount(e.target.value)}
            renderValue={(selected) => {
              const selectedIntegration = integrations.find(
                (i) => i.id === selected
              );
              if (!selectedIntegration) return "";

              const logo = getIntegrationLogo(selectedIntegration.type);
              return (
                <Box display="flex" alignItems="center" gap={1}>
                  {logo && (
                    <Avatar
                      src={logo}
                      sx={{ width: 20, height: 20 }}
                      variant="square"
                    />
                  )}
                  {selectedIntegration.name}
                </Box>
              );
            }}
          >
            {integrations.map((integration) => {
              const logo = getIntegrationLogo(integration.type);
              return (
                <MenuItem key={integration.id} value={integration.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {logo && (
                      <Avatar
                        src={logo}
                        sx={{ width: 20, height: 20 }}
                        variant="square"
                      />
                    )}
                    <Box>
                      <Typography variant="body2">
                        {integration.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {integration.type
                          ? integration.type.charAt(0).toUpperCase() +
                            integration.type.slice(1)
                          : ""}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2 }}
        >
          💡 Each content item will be scheduled using its existing publish date
          and the first image.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSchedule}
          loading={isScheduling}
          disabled={!publishAccount}
          variant="contained"
        >
          Schedule
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
